import type { Spritesheet } from "pixi.js";
import type { SymbolCatalogEntry } from "./symbolCatalog";
import type { Log, SymbolDefinition, SymbolDefinitionResolver } from "@slot/presentation";

export class RuntimeSymbolCatalog implements SymbolDefinitionResolver {
  private readonly map: Map<string, SymbolDefinition>;
  /** symbolId → the symbol's pre-blurred FILLER definition (base def + the entry's blurredFrameName). */
  private readonly fillerBlur: Map<string, SymbolDefinition>;

  public constructor(map: Map<string, SymbolDefinition>, fillerBlur: Map<string, SymbolDefinition> = new Map()) {
    this.map = map;
    this.fillerBlur = fillerBlur;
  }

  /**
   * Build the catalog from the per-game SYMBOL_CATALOG config. Each entry's crisp + pre-blurred
   * FILLER frames are referenced BY NAME and resolved here against the injected symbols
   * spritesheet. A missing entry id / frame ⇒ that symbol is skipped (logged), never throws.
   */
  public static fromConfig(input: {
    entries: readonly SymbolCatalogEntry[];
    atlas: Spritesheet;
    log?: Log;
  }): RuntimeSymbolCatalog {
    const next = new Map<string, SymbolDefinition>();
    const fillerBlur = new Map<string, SymbolDefinition>();
    const log = input.log ?? (() => {});

    for (const e of input.entries) {
      const symbolId = (e.symbolId ?? "").trim();
      if (!symbolId) continue;

      const frameName = (e.frameName ?? "").trim();
      if (!frameName) {
        log(`[RuntimeSymbolCatalog] entry "${symbolId}" is missing frameName`);
        continue;
      }

      const texture = input.atlas.textures[frameName];
      if (!texture) {
        log(`[RuntimeSymbolCatalog] atlas missing frame "${frameName}" for symbolId="${symbolId}"`);
        continue;
      }

      const baseScale = Number.isFinite(e.baseScale) ? e.baseScale : 1;

      const definition: SymbolDefinition = {
        symbolId,
        texture,
        baseScale,
        fitMode: e.fitMode,
        offsetX: e.offsetX ?? 0,
        offsetY: e.offsetY ?? 0,
        landSoundKey: (e.landSoundKey ?? "").trim() || null,
        suppressDefaultDropSound: e.suppressDefaultDropSound,
      };
      next.set(symbolId, definition);

      const blurredFrameName = (e.blurredFrameName ?? "").trim();
      if (blurredFrameName) {
        const blurredTexture = input.atlas.textures[blurredFrameName];
        if (blurredTexture) {
          const blurredScale = e.blurredScale ?? 1;
          fillerBlur.set(symbolId, {
            ...definition,
            texture: blurredTexture,
            baseScale: baseScale * blurredScale,
            offsetX: (definition.offsetX ?? 0) + (e.blurredOffsetX ?? 0),
            offsetY: (definition.offsetY ?? 0) + (e.blurredOffsetY ?? 0),
          });
        } else {
          log(`[RuntimeSymbolCatalog] atlas missing blurred frame "${blurredFrameName}" for symbolId="${symbolId}"`);
        }
      }
    }

    return new RuntimeSymbolCatalog(next, fillerBlur);
  }

  public resolveDefinition(symbolId: string): SymbolDefinition | null {
    return this.map.get(symbolId) ?? null;
  }

  /** Per-symbol pre-blurred FILLER definitions, consumed by the reel drum. */
  public fillerBlurDefinitions(): Map<string, SymbolDefinition> {
    return this.fillerBlur;
  }
}
